import { isEncounterStatus, type EncounterStatus } from '@emr/shared';

import type { BadgeTone } from '../../components/primitives.tsx';

/**
 * A `Badge` tone for each of `@emr/shared`'s `ENCOUNTER_STATUSES` — pulled out of
 * `tabs/EncountersNotesTab.tsx` into its own tested module, matching the shape this diff already
 * uses for `chartAccess.ts` (`ChartAccessDenialReason` → copy) and `noteActions.ts` (note state →
 * availability): a status→presentation mapping is real logic, not markup, and belongs in a module
 * with its own test rather than inline in the component that happens to render it first.
 *
 * `Record<EncounterStatus, BadgeTone>` is the forcing function, matching `AccessPage.tsx`'s
 * `CAPABILITY_COPY` and `VitalsTab.tsx`'s `VITAL_TYPE_LABELS`: a status added to the shared union
 * without a row here fails the build rather than silently falling back for a known value.
 */
const ENCOUNTER_STATUS_TONE: Record<EncounterStatus, BadgeTone> = {
  scheduled: 'neutral',
  in_progress: 'accent',
  completed: 'success',
  cancelled: 'warning',
};

/** A tone for `status`, or the neutral fallback for a value this build does not recognise —
 * `encounters.status` is `z.string()` on the wire (`shared/src/encounterRequests.ts`), not the
 * closed enum itself, so an unrecognised value is a real possibility, not a defect to assume can
 * never happen (the same reasoning `VitalsTab.tsx`'s `vitalTypeLabel` already applies). */
export function encounterStatusTone(status: string): BadgeTone {
  return isEncounterStatus(status) ? ENCOUNTER_STATUS_TONE[status] : 'neutral';
}
